import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

export const STATIC_PAGE_BACKGROUND_URL =
  "https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80";

type TStaticPageLayoutProps = {
  children: ReactNode;
  /** Use min-h vs fixed h for the container */
  fullHeight?: boolean;
  className?: string;
  /** centered: hero pages (404). document: fixed-height legal/static content with internal scroll */
  variant?: "centered" | "document";
};

/**
 * Shared layout for static content pages (Terms, Privacy, NotFound, Error).
 * Provides the blurred background image + dark overlay + content area.
 */
export function StaticPageLayout({
  children,
  fullHeight = true,
  className,
  variant = "centered",
}: TStaticPageLayoutProps) {
  const isDocument = variant === "document";

  return (
    <article
      className={cn(
        "relative overflow-hidden",
        isDocument
          ? "h-[calc(100vh-var(--footer-height))]"
          : fullHeight
            ? "min-h-[calc(100vh-var(--footer-height))]"
            : "h-[calc(100vh-var(--footer-height))]",
        className
      )}
    >
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('${STATIC_PAGE_BACKGROUND_URL}')`,
          filter: "blur(4px)",
          transform: "scale(1.1)",
        }}
      />
      <div className="absolute inset-0 bg-black/55" />
      <section
        className={cn(
          "relative z-10 min-h-full px-4",
          isDocument
            ? "flex h-full flex-col pb-6 pt-[calc(var(--navbar-height)+1rem)]"
            : "flex items-center justify-center py-12 pt-[calc(var(--navbar-height)+1rem)]"
        )}
      >
        {children}
      </section>
    </article>
  );
}
